// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EducationalTooltip } from '@/components/educational-tooltip';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  Add as AddIcon,
  DownloadRounded,
  InfoOutlined,
  SwapHoriz as SwapHorizIcon,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { BulkItemInventoryItem } from '@/inventory/api/types';
import { useInventory, useInventoryDimensionFilter } from '@/inventory/hooks';
import { exportBulkItemInventoryCsv } from '@/inventory/utilities/export-bulk-item-inventory-csv';
import { AdjustInventoryDialog } from '@/items/components/adjust-inventory-dialog';
import { AdjustInventoryProvider } from '@/items/components/adjust-inventory-provider';
import { InventoryTable } from '@/items/components/inventory-table';
import { InventoryViewSelector } from '@/items/components/inventory-view-selector';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import { TransferInventoryDialog } from '@/items/components/transfer-inventory-dialog';
import { TransferInventoryProvider } from '@/items/components/transfer-inventory-provider';
import { useAdjustInventory } from '@/items/hooks/use-adjust-inventory';
import { useTransferInventory } from '@/items/hooks/use-transfer-inventory';
import { LocationFilterMenu } from '@/location/components/location-filter-menu';
import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

/**
 * Search parameters for inventory page
 */
const searchSchema = z.object({
  itemKey: z.array(z.string()).optional(),
  locationId: z.array(z.string()).optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Route configuration for inventory page
 */
export const Route = createFileRoute('/_app/inventory')({
  component: InventoryRouteWithDialog,
  validateSearch: searchSchema,
});

/**
 * Main inventory page component
 */
function InventoryRoute() {
  const search = Route.useSearch() as SearchParams;
  const navigate = Route.useNavigate();
  const { openDialog: openAdjustDialog } = useAdjustInventory();
  const { openDialog: openTransferDialog } = useTransferInventory();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  // State to hold export data from InventoryContent
  const [exportData, setExportData] = useState<BulkItemInventoryItem[]>([]);

  const handleChangeItemFilter = (value: string[]) => {
    navigate({
      search: {
        ...search,
        itemKey: value.length > 0 ? value : undefined,
      },
    });
  };

  const handleChangeLocationFilter = (value: string[]) => {
    navigate({
      search: {
        ...search,
        locationId: value.length > 0 ? value : undefined,
      },
    });
  };

  // Callback to receive export data from InventoryContent
  const handleExportDataChange = useCallback(
    (data: BulkItemInventoryItem[]) => {
      setExportData(data);
    },
    []
  );

  // Handle export button click
  const handleExport = () => {
    if (exportData.length > 0) {
      exportBulkItemInventoryCsv(exportData);
    }
  };

  return (
    <PageContent>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">Parts inventory</Typography>
            <IconButton
              ref={infoButtonRef}
              size="small"
              onClick={infoTooltip.open}
              aria-label="Learn about inventory"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadRounded fontSize="small" />}
              onClick={handleExport}
              disabled={exportData.length === 0}
            >
              Export data
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() =>
                openAdjustDialog({
                  item: undefined,
                  location: undefined,
                })
              }
            >
              Adjust
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHorizIcon fontSize="small" />}
              onClick={() =>
                openTransferDialog({
                  item: undefined,
                  location: undefined,
                })
              }
            >
              Transfer
            </Button>
          </Stack>
        </Stack>

        {/* Educational tooltip explaining inventory */}
        <EducationalTooltip
          title="Parts inventory"
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Parts inventory</strong> tracks items at any location.
                Any location can store quantities of items from your item
                catalog. Inventory can be used directly, or can be the second
                step in the <strong>inventory workflow</strong> as items move
                toward becoming production assets.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Viewing parts inventory
              </Typography>
              <Typography variant="body2">
                Inventory can be viewed by <strong>item</strong> or by{' '}
                <strong>location</strong>. You can drill down for each area to
                understand the number of units of each item at each location.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Managing parts inventory
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
              >
                <li>
                  <strong>Transfer:</strong> Move units between inventory
                  locations. Transfers take effect immediately and do not
                  introduce new units—they decrement from one location and
                  increment at another.
                </li>
                <li>
                  <strong>Adjust:</strong> Adjust unit counts up or down for
                  inventory reconciliation. Adjusting up introduces new units,
                  while adjusting down removes existing units.
                </li>
              </Typography>
            </Box>
            <Typography variant="body2">
              <Link
                href="https://guide.hardfin.com/source-and-build/inventory"
                target="_blank"
              >
                Read more about inventory
              </Link>
            </Typography>
          </Stack>
        </EducationalTooltip>
        <InventoryContent
          selectedItemKeys={search.itemKey || []}
          locationIDs={search.locationId || []}
          onChangeItemFilter={handleChangeItemFilter}
          onChangeLocationFilter={handleChangeLocationFilter}
          onExportDataChange={handleExportDataChange}
        />
      </Stack>
    </PageContent>
  );
}

interface InventoryContentProps {
  locationIDs: string[];
  selectedItemKeys: string[];
  onChangeItemFilter: (value: string[]) => void;
  onChangeLocationFilter: (value: string[]) => void;
  onExportDataChange: (data: BulkItemInventoryItem[]) => void;
}

function InventoryContent(props: InventoryContentProps) {
  const { dimension, onChangeDimension } = useInventoryDimensionFilter();
  const result = useInventory({
    type: 'bulk-items',
    dimension,
    filters: {
      itemKeys: props.selectedItemKeys,
      locationIds: props.locationIDs,
    },
  });

  // Get the raw flat data for export purposes
  const rawData = useMemo(() => {
    if (result.view === 'bulk-items') {
      return (result.inventory?.data.results ?? []) as BulkItemInventoryItem[];
    }
    // For location view, we need to fetch the bulk item data separately for export
    // but for now we'll return empty since the data structure is different
    return [] as BulkItemInventoryItem[];
  }, [result.view, result.inventory]);

  // Notify parent of export data changes
  const onExportDataChange = props.onExportDataChange;
  useEffect(() => {
    onExportDataChange(rawData);
  }, [rawData, onExportDataChange]);

  // Extract and transform data based on the view type returned
  const data = useMemo(() => {
    if (result.view === 'location') {
      // Location view returns already grouped data
      return result.inventory?.data.results ?? [];
    }

    if (result.view === 'bulk-items') {
      // Item view returns flat data that needs to be grouped by item
      const flatItems = result.inventory?.data.results ?? [];
      const itemMap = new Map<
        string,
        {
          item: (typeof flatItems)[number]['item'];
          locations: {
            location: (typeof flatItems)[number]['location'];
            inventory: (typeof flatItems)[number]['inventory'];
          }[];
        }
      >();

      for (const item of flatItems) {
        if (!itemMap.has(item.item.key)) {
          itemMap.set(item.item.key, {
            item: item.item,
            locations: [],
          });
        }
        const itemEntry = itemMap.get(item.item.key);
        if (itemEntry) {
          itemEntry.locations.push({
            location: item.location,
            inventory: item.inventory,
          });
        }
      }

      return Array.from(itemMap.values());
    }

    // Fallback for unexpected view types
    return [];
  }, [result.view, result.inventory]);

  return (
    <>
      <TableControlContainer>
        <Stack
          useFlexGap
          spacing={1}
          flexWrap="wrap"
          direction="row"
          marginRight="auto"
        >
          <InventoryViewSelector
            value={dimension}
            onChange={onChangeDimension}
          />
          <ItemFilterMenu
            label="Item"
            value={props.selectedItemKeys}
            onChange={props.onChangeItemFilter}
          />
          <LocationFilterMenu
            label="Location"
            value={props.locationIDs}
            onChange={props.onChangeLocationFilter}
          />
        </Stack>
      </TableControlContainer>

      <SupportErrorBoundary message="Failed to load inventory">
        <InventoryTable
          view={dimension}
          data={data}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
        />
      </SupportErrorBoundary>
    </>
  );
}

/**
 * Wrapper component that includes the inventory route and dialog
 * This is exported temporarily to prevent it from throwing lint errors
 */
function InventoryRouteWithDialog() {
  return (
    <AdjustInventoryProvider>
      <TransferInventoryProvider>
        <InventoryRouteWithDialogs />
      </TransferInventoryProvider>
    </AdjustInventoryProvider>
  );
}

/**
 * Inner component that uses the inventory context providers
 */
function InventoryRouteWithDialogs() {
  const {
    isOpen: isAdjustOpen,
    item: adjustItem,
    location: adjustLocation,
    closeDialog: closeAdjustDialog,
  } = useAdjustInventory();
  const {
    isOpen: isTransferOpen,
    item: transferItem,
    location: transferLocation,
    closeDialog: closeTransferDialog,
  } = useTransferInventory();

  return (
    <>
      <InventoryRoute />
      <AdjustInventoryDialog
        isOpen={isAdjustOpen}
        item={adjustItem}
        location={adjustLocation}
        onClose={closeAdjustDialog}
      />
      <TransferInventoryDialog
        isOpen={isTransferOpen}
        item={transferItem}
        location={transferLocation}
        onClose={closeTransferDialog}
      />
    </>
  );
}
